import React, { useEffect, useState } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { useMutation } from '@apollo/react-hooks';
import { useForm } from 'react-hook-form';

// services
import { getUtmParamsMap } from '../../services/tracking/tracking';
import { formatGraphQLError } from '../../services/format';
import { getCookie } from '../../services/cookies';
// queries
import { SEND_MAGIC_LINK } from '@queries/user';
// components
import CustomInput from '@components/form/custom-input';
import SubmitButton from '@components/submit-button';
import Loader from '@components/loader';
import EmailLoader from './EmailLoader/email-loader';
// styles
import styles from './sign-in-form.module.scss';
import MagicLinkView from './MagicLinkView/MagicLinkView';
import { COOKIES } from '@constants/cookies';
import { getSessionId } from '@services/session';

const ANIMATION_TIMEOUT = 7000;

interface IProps {
  destination?: string;
  setIsMagicLinkView?: (state: boolean) => void;
}

interface ResetEmail {
  link: string;
  isVisible: boolean;
}

const SignInForm: React.FC<IProps> = ({
  destination = '/account/dashboard',
  setIsMagicLinkView,
}) => {
  const content = useStaticQuery(graphql`
    query SignIn {
      directusAccount {
        signin_email_label
        signin_signin_button_label
        signin_no_account
        signin_register_button_label
      }
    }
  `);

  const { directusAccount } = content;

  const [isSendingEmailAnimation, setIsSendingEmailAnimation] = useState<boolean>(false);

  const [email, setEmail] = useState<ResetEmail>({
    link: '',
    isVisible: false,
  });

  useEffect(() => {
    if (!isSendingEmailAnimation) {
      return;
    }

    const timer = setTimeout(function () {
      setIsSendingEmailAnimation(false);
    }, ANIMATION_TIMEOUT);

    return () => clearTimeout(timer);
  }, [isSendingEmailAnimation]);

  // state
  const {
    register,
    handleSubmit,
    errors,
    formState: { dirtyFields },
  } = useForm({ shouldUnregister: false });

  const [
    sendMagicLink,
    { data: dataMagicLink, loading: loadingMagicLink, error: errorMagicLink },
  ] = useMutation(SEND_MAGIC_LINK);

  const onSubmit = (formData: any) => {
    const basketId = getCookie(COOKIES.basketId);
    setIsSendingEmailAnimation(true);
    setIsMagicLinkView(true);

    setEmail({ link: formData.email, isVisible: true });
    const utmQueryParamsMap = getUtmParamsMap();
    const sessionId = getSessionId();

    const destinationUrl = new URL(`${process.env.GATSBY_HOST_NAME!}${destination}`);
    const userParams = [
      { key: 'basketId', value: basketId },
      { key: 'sessionId', value: sessionId },
      ...utmQueryParamsMap,
    ];
    userParams.forEach(param => {
      if (param.value) {
        destinationUrl.searchParams.append(param.key, param.value);
      }
    });

    const destinationUrlEncoded = encodeURIComponent(destinationUrl.toString());

    const destinationMagicLink = `${process.env.GATSBY_HOST_NAME}/account/signin?destination=${destinationUrlEncoded}`;

    sendMagicLink({
      variables: {
        email: formData.email,
        destination: destinationMagicLink,
        leadAttribution: getUtmParamsMap(),
      },
    });
  };

  const message = 'A sign in link has been sent to: ';

  if (!directusAccount) return <Loader />;
  if (isSendingEmailAnimation) return <EmailLoader />;
  if (!isSendingEmailAnimation && email.isVisible) {
    return (
      <MagicLinkView
        message={message}
        email={email.link}
        goBack={() => {
          setIsMagicLinkView(false);
          setEmail({ link: email.link, isVisible: false });
        }}
      />
    );
  }

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <fieldset disabled={loadingMagicLink} className={styles.fieldset}>
          <CustomInput
            id="email"
            type="email"
            register={register({ required: true })}
            label={directusAccount.signin_email_label}
            isDirty={dirtyFields.email}
            error={errors.email}
            errorMessage="Please enter your email address"
          />
        </fieldset>

        <SubmitButton
          loading={loadingMagicLink || isSendingEmailAnimation}
          success={dataMagicLink}
          successMessage={isSendingEmailAnimation ? '' : `${message}${email.link}`}
          error={errorMagicLink}
          errorMessage={errorMagicLink && formatGraphQLError(errorMagicLink)}
          buttonLabel="Email me a login link"
          fullWidth
          className={styles.submitButton}
        />
      </form>
    </div>
  );
};

export default SignInForm;
